import { computed, type Signal, signal } from "@lit-labs/signals";
import * as Sentry from "@sentry/browser";
import { proxy, releaseProxy, transfer, wrap } from "comlink";
import { reaction } from "signal-utils/subtle/reaction";
import {
  installWorkerErrorReceivers,
  uninstallWorkerErrorReceivers,
} from "../utils/errors.ts";
import { installIOSWorkerHeartbeat } from "../utils/ios-worker-heartbeat.ts";
import { nextAnimationFrame } from "../utils/timing.ts";
import { puzzleAugmentations } from "./augmentation.ts";
import { puzzleDataMap } from "./catalog.ts";
import type {
  ChangeNotification,
  Colour,
  ConfigDescription,
  ConfigValues,
  FontInfo,
  GameStatus,
  KeyLabel,
  Point,
  PresetMenuEntry,
  PuzzleStaticAttributes,
  Size,
} from "./types.ts";
import type { RemoteWorkerPuzzle, RemoteWorkerPuzzleFactory } from "./worker.ts";

const sentryWebWorkerIntegration = import.meta.env.VITE_SENTRY_DSN
  ? Sentry.webWorkerIntegration({ worker: [] })
  : null;
if (sentryWebWorkerIntegration) {
  Sentry.addIntegration(sentryWebWorkerIntegration);
}

/**
 * Public API to the remote WASM puzzle module running in a worker.
 * Exposes reactive properties for puzzle state.
 * Exposes async methods for calling WASM Frontend APIs.
 */
export class Puzzle {
  public static async create(puzzleId: string): Promise<Puzzle> {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setTag("puzzleId", puzzleId);
    }
    const worker = new Worker(new URL("./worker.ts", import.meta.url), {
      type: "module",
      name: `puzzle-worker-${puzzleId}`,
    });
    if (sentryWebWorkerIntegration) {
      sentryWebWorkerIntegration.addWorker(worker);
      // Handle forwarded event enrichment data from worker
      worker.addEventListener("message", (event: MessageEvent<unknown>) => {
        if (
          typeof event.data === "object" &&
          event.data !== null &&
          "type" in event.data &&
          event.data.type === "sentry-breadcrumb" &&
          "breadcrumb" in event.data &&
          typeof event.data.breadcrumb === "object" &&
          event.data.breadcrumb !== null
        ) {
          Sentry.addBreadcrumb(event.data.breadcrumb);
        }
      });
    }
    installWorkerErrorReceivers(worker);
    const workerFactory = wrap<RemoteWorkerPuzzleFactory>(worker);
    const workerPuzzle = await workerFactory.create(puzzleId);

    const staticProps = await workerPuzzle.getStaticProperties();
    const puzzle = new Puzzle(puzzleId, worker, workerPuzzle, staticProps);
    await puzzle.initialize();
    return puzzle;
  }

  private readonly disposers: (() => void)[] = [];

  // Private constructor; use Puzzle.create(puzzleId) to instantiate a Puzzle.
  private constructor(
    public readonly puzzleId: string,
    private readonly worker: Worker,
    private readonly workerPuzzle: RemoteWorkerPuzzle,
    {
      displayName,
      canConfigure,
      canSolve,
      needsRightButton,
      isTimed,
      wantsStatusbar,
    }: PuzzleStaticAttributes,
  ) {
    const catalogData = puzzleDataMap[puzzleId];
    // Prefer catalog name to midend API name
    // (e.g., catalog "Tracks" vs API "Train Tracks")
    this.displayName = catalogData?.name ?? displayName;
    this.isUnfinished = catalogData?.unfinished ?? false;
    this.canConfigure = canConfigure;
    this.canSolve = canSolve;
    this.needsRightButton = needsRightButton;
    this.isTimed = isTimed;
    this.wantsStatusbar = wantsStatusbar;

    // Prevent worker suspension when page is visible
    this.disposers.push(installIOSWorkerHeartbeat(this.worker));
    this.initializeAnalytics();
  }

  private async initialize(): Promise<void> {
    await this.workerPuzzle.setCallbacks(
      proxy(this.notifyChange),
      proxy(this.notifyTimerState),
    );
  }

  public async delete(): Promise<void> {
    await this.detachCanvas();
    await this.workerPuzzle.delete();
    this.workerPuzzle[releaseProxy]();
    while (this.disposers.length > 0) {
      this.disposers.pop()?.();
    }
    uninstallWorkerErrorReceivers(this.worker);
    this.worker.terminate();
  }

  private _size = "<unknown>";

  private captureSentryContext() {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setContext("Puzzle", {
        "Puzzle ID": this.puzzleId,
        Params: this.params,
        "Game ID": this.currentGameId,
        "Random Seed": this.randomSeed,
        "Current Move": this.currentMove,
        "Total Moves": this.totalMoves,
        Size: this._size,
      });
    }
  }

  private initializeAnalytics() {
    if (import.meta.env.VITE_SENTRY_DSN) {
      // Count solved/lost events
      this.disposers.push(
        reaction(
          () => this.status,
          (status, _prevStatus) => {
            const metric = {
              solved: "gameSolved",
              "solved-with-help": "gameSolved",
              lost: "gameLost",
              ongoing: null,
            }[status];
            if (metric) {
              console.log(`Tracking ${metric}`);
              Sentry.metrics.count(metric, 1, {
                attributes: {
                  "puzzle.id": this.puzzleId,
                  "puzzle.type": this.currentParams,
                  "puzzle.moveCount": this.currentMove,
                  // "puzzle.usedHints": status === "solved-with-help",
                },
              });
            }
          },
        ),
      );
    }
  }

  private notifyChange = async (message: ChangeNotification) => {
    // Callback from C++ Frontend: update signals with provided data.
    // (Message originates in worker.)
    function update<T>(signal: Signal.State<T>, newValue: T) {
      if (signal.get() !== newValue) {
        signal.set(newValue);
      }
    }

    switch (message.type) {
      case "game-id-change": {
        update(this._currentGameId, message.currentGameId);
        update(this._randomSeed, message.randomSeed);
        break;
      }
      case "game-state-change":
        this.purgeInvalidCheckpoints(message.totalMoves);
        update(this._status, message.status);
        update(this._currentMove, message.currentMove);
        update(this._totalMoves, message.totalMoves);
        update(this._canUndo, message.canUndo);
        update(this._canRedo, message.canRedo);
        break;
      case "params-change":
        update(this._params, message.params);
        break;
      case "status-bar-change":
        update(this._statusbarText, message.statusBarText);
        break;
      default:
        // @ts-expect-error: message.type never
        throw new Error(`Unknown notifyChange type ${message.type}`);
    }

    this.captureSentryContext();
  };

  private inputQueue: Promise<void> = Promise.resolve();

  /**
   * Keep events that end up in midend_process_key() strictly ordered.
   */
  protected enqueueInput<T>(fn: () => Promise<T>): Promise<T> {
    const result = this.inputQueue.then(fn);
    this.inputQueue = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  // Static properties (no reactivity needed)
  public readonly displayName: string;
  public readonly isUnfinished: boolean; // "experimental" puzzle status
  public readonly canConfigure: boolean;
  public readonly canSolve: boolean;
  public readonly needsRightButton: boolean;
  public readonly isTimed: boolean;
  public readonly wantsStatusbar: boolean;

  // Reactive properties
  private _status = signal<GameStatus>("ongoing");
  private _currentMove = signal<number>(0);
  private _totalMoves = signal<number>(0);
  private _canUndo = signal(false);
  private _canRedo = signal(false);
  private _params = signal<string>("");
  private _currentParams = computed<string | undefined>(
    () =>
      // The encoded params are in randomSeed before '#' and currentGameId before ':'.
      // The randomSeed version is more descriptive if available (e.g, includes difficulty).
      this.randomSeed?.split("#", 1).at(0) ?? this.currentGameId?.split(":", 1).at(0),
  );
  private _currentGameId = signal<string | undefined>(undefined);
  private _randomSeed = signal<string | undefined>(undefined);
  private _canFormatAsText = signal(false);
  private _statusbarText = signal<string>("");
  private _generatingGame = signal<boolean>(false);

  public get status(): GameStatus {
    return this._status.get();
  }

  public get isSolved(): boolean {
    return this.status === "solved" || this.status === "solved-with-help";
  }

  public get currentMove(): number {
    return this._currentMove.get();
  }

  public get totalMoves(): number {
    return this._totalMoves.get();
  }

  public get canUndo(): boolean {
    return this._canUndo.get();
  }

  public get canRedo(): boolean {
    return this._canRedo.get();
  }

  // The encoded game params that will be used for the next "new game".
  public get params(): string {
    return this._params.get();
  }

  // The encoded game params in effect for the current game.
  public get currentParams(): string | undefined {
    return this._currentParams.get();
  }

  public get currentGameId(): string | undefined {
    return this._currentGameId.get();
  }

  public get randomSeed(): string | undefined {
    return this._randomSeed.get();
  }

  public get canFormatAsText(): boolean {
    return this._canFormatAsText.get();
  }

  public get statusbarText(): string | null {
    return this._statusbarText.get();
  }

  public get generatingGame(): boolean {
    return this._generatingGame.get();
  }

  // Methods
  public async newGame(): Promise<void> {
    if (import.meta.env.VITE_SENTRY_DSN) {
      console.log("Tracking newGame");
      Sentry.metrics.count("newGame", 1, {
        attributes: {
          "puzzle.id": this.puzzleId,
          "puzzle.type": this.currentParams,
        },
      });
    }

    this._generatingGame.set(true);
    const startTime = performance.now();
    await this.workerPuzzle.newGame();
    const duration = performance.now() - startTime;
    this._generatingGame.set(false);

    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.metrics.distribution("gameGenerationTime", duration, {
        unit: "millisecond",
        attributes: {
          "puzzle.id": this.puzzleId,
          "puzzle.type": this.currentParams,
        },
      });
    }
  }

  public async newGameFromId(id: string): Promise<string | undefined> {
    return this.workerPuzzle.newGameFromId(id);
  }

  public async restartGame(): Promise<void> {
    await this.workerPuzzle.restartGame();
  }

  public undo(): Promise<void> {
    return this.enqueueInput(() => this.workerPuzzle.undo());
  }

  public redo(): Promise<void> {
    return this.enqueueInput(() => this.workerPuzzle.redo());
  }

  public async solve(): Promise<string | undefined> {
    return this.workerPuzzle.solve();
  }

  public processKey(key: number): Promise<boolean> {
    return this.enqueueInput(() => this.workerPuzzle.processKey(key));
  }

  public processMouse({ x, y }: Point, button: number): Promise<boolean> {
    return this.enqueueInput(() => this.workerPuzzle.processMouse({ x, y }, button));
  }

  public async requestKeys(): Promise<KeyLabel[]> {
    return this.workerPuzzle.requestKeys();
  }

  public async getParams(): Promise<string> {
    return this.workerPuzzle.getParams();
  }

  public async setParams(params: string): Promise<string | undefined> {
    return this.workerPuzzle.setParams(params);
  }

  public async getParamsDescription(params: string): Promise<string> {
    // First try preset names
    const presets = await this.getPresets(true);
    const preset = presets.find((preset) => preset.params === params);
    if (preset) {
      return preset.title;
    }

    // Next try augmentations
    const augmentation = puzzleAugmentations[this.puzzleId];
    if (augmentation?.describeConfig) {
      const config = await this.decodeCustomParams(params);
      if (typeof config === "string") {
        return `ERROR: '${params}': ${config}`;
      }
      return augmentation.describeConfig(config);
    }

    // Give up
    return "Custom type";
  }

  public async getPresets(flat = false): Promise<PresetMenuEntry[]> {
    let presets = await this.workerPuzzle.getPresets();
    if (flat) {
      const flatten = (items: PresetMenuEntry[]): PresetMenuEntry[] => {
        return items.flatMap((item) => [
          item,
          ...(item.submenu ? flatten(item.submenu) : []),
        ]);
      };
      presets = flatten(presets);
    }
    return presets;
  }

  public async getCustomParamsConfig(): Promise<ConfigDescription> {
    return this.workerPuzzle.getCustomParamsConfig();
  }

  public async getCustomParams(): Promise<ConfigValues> {
    return this.workerPuzzle.getCustomParams();
  }

  public async setCustomParams(values: ConfigValues): Promise<string | undefined> {
    return this.workerPuzzle.setCustomParams(values);
  }

  public async decodeCustomParams(params: string): Promise<ConfigValues | string> {
    return this.workerPuzzle.decodeCustomParams(params);
  }

  public async encodeCustomParams(values: ConfigValues): Promise<string> {
    return this.workerPuzzle.encodeCustomParams(values);
  }

  public async getPreferencesConfig(): Promise<ConfigDescription> {
    return this.workerPuzzle.getPreferencesConfig();
  }

  public async getPreferences(): Promise<ConfigValues> {
    return this.workerPuzzle.getPreferences();
  }

  public async savePreferences(): Promise<Uint8Array> {
    return this.workerPuzzle.savePreferences();
  }

  public async loadPreferences(data: Uint8Array): Promise<string | undefined> {
    return this.workerPuzzle.loadPreferences(transfer(data, [data.buffer]));
  }

  public async setPreferences(values: ConfigValues): Promise<string | undefined> {
    return this.workerPuzzle.setPreferences(values);
  }

  public async redraw(): Promise<void> {
    if (!this.hasSize) {
      // "Some back ends require that midend_size() is called before midend_redraw()."
      console.error("Ignoring Puzzle.redraw() called before Puzzle.size()");
      return;
    }
    await this.workerPuzzle.redraw();
  }

  public async getColourPalette(defaultBackground: Colour): Promise<Colour[]> {
    return this.workerPuzzle.getColourPalette(defaultBackground);
  }

  // Whether size() has been successfully called yet.
  private hasSize = false;

  public async size(
    maxSize: Size,
    isUserSize: boolean,
    devicePixelRatio: number,
  ): Promise<Size> {
    if (!this.currentGameId) {
      // "The midend relies on the frontend calling midend_new_game() before calling
      // midend_size()." (Or otherwise having a game, e.g., midend_deserialise().)
      console.error("Ignoring Puzzle.size() called before game initialized");
      return maxSize;
    }
    const result = await this.workerPuzzle.size(maxSize, isUserSize, devicePixelRatio);
    this.hasSize = true;
    return result;
  }

  public async preferredSize(): Promise<Size> {
    return this.workerPuzzle.preferredSize();
  }

  public async formatAsText(): Promise<string | undefined> {
    return this.workerPuzzle.formatAsText();
  }

  public async loadGame(data: Uint8Array<ArrayBuffer>): Promise<string | undefined> {
    return this.workerPuzzle.loadGame(transfer(data, [data.buffer]));
  }

  public async saveGame(): Promise<Uint8Array<ArrayBuffer>> {
    const result = this.workerPuzzle.saveGame();
    if (import.meta.env.VITE_SENTRY_DSN) {
      // Capture the most recent (auto-)save as a Sentry attachment.
      // (There's no way to replace a specific attachment, so just clear all.)
      Sentry.getCurrentScope().clearAttachments();
      Sentry.getCurrentScope().addAttachment({
        filename: "save.txt",
        data: await result,
        contentType: "text/plain",
      });
    }
    return result;
  }

  //
  // Checkpoints
  //

  private _checkpoints = signal<ReadonlySet<number>>(new Set());

  /**
   * A set of move numbers that have been set as checkpoints.
   */
  get checkpoints(): ReadonlySet<number> {
    return this._checkpoints.get();
  }

  set checkpoints(value: Iterable<number>) {
    this._checkpoints.set(new Set(value));
  }

  /**
   * Set a checkpoint at move (default the current move).
   */
  public addCheckpoint(move?: number) {
    const checkpoint = move ?? this.currentMove;
    if (!this.checkpoints.has(checkpoint)) {
      // TODO: use reactive set (from signal-utils) rather than replacing value
      const newCheckpoints = new Set(this.checkpoints);
      newCheckpoints.add(checkpoint);
      this._checkpoints.set(newCheckpoints);
    }
  }

  /**
   * Remove checkpoint if it exists
   */
  public removeCheckpoint(checkpoint: number) {
    if (this.checkpoints.has(checkpoint)) {
      // TODO: use reactive set (from signal-utils) rather than replacing value
      const newCheckpoints = new Set(this.checkpoints);
      newCheckpoints.delete(checkpoint);
      this._checkpoints.set(newCheckpoints);
    }
  }

  /**
   * Wind the game forward/backward to move number checkpoint.
   * (Checkpoint can actually be any valid move number,
   * and does not have to have been saved as a checkpoint.)
   */
  public async goToCheckpoint(checkpoint: number): Promise<void> {
    if (checkpoint < 0 || checkpoint > this.totalMoves) {
      throw new RangeError(`Move ${checkpoint} out of bounds`);
    }
    const delta = checkpoint - this.currentMove;
    if (delta < 0) {
      for (let i = 0; i < -delta; i++) {
        await this.undo();
      }
    } else if (delta > 0) {
      for (let i = 0; i < delta; i++) {
        await this.redo();
      }
    }
  }

  private purgeInvalidCheckpoints(totalMoves: number) {
    // Called before updating this.currentMove and this.totalMoves.
    // Prune any checkpoints past new totalMoves.
    if (totalMoves < this.totalMoves && this.checkpoints.size > 0) {
      // TODO: use reactive set (from signal-utils) rather than replacing value
      const newCheckpoints = new Set(this.checkpoints);
      for (const checkpoint of newCheckpoints) {
        // BUG: this can't distinguish these two cases:
        //   - set checkpoint; undo; redo (shouldn't purge checkpoint == totalMoves)
        //   - set checkpoint; undo; move (_should_ purge checkpoint >= totalMoves)
        // To avoid unexpected purging, use `>` rather than `>=`:
        if (checkpoint > totalMoves) {
          newCheckpoints.delete(checkpoint);
        }
      }
      if (newCheckpoints.size < this.checkpoints.size) {
        this._checkpoints.set(newCheckpoints);
      }
    }
  }

  //
  // Public API to Drawing
  //

  public async attachCanvas(
    canvas: OffscreenCanvas,
    fontInfo: FontInfo,
  ): Promise<void> {
    // Transfer the canvas to the worker
    await this.workerPuzzle.attachCanvas(transfer(canvas, [canvas]), fontInfo);
    // Delay one frame to avoid a problem in Safari and Firefox where the
    // onscreen canvas initially (and somewhat randomly) appears solid black
    // or solid background color. (Seems like drawing to the offscreen canvas
    // immediately after transfer to the worker doesn't make it back onscreen.)
    await nextAnimationFrame();
  }

  public async detachCanvas(): Promise<void> {
    await this.workerPuzzle.detachCanvas();
  }

  public async resizeDrawing({ w, h }: Size, dpr: number): Promise<void> {
    if (import.meta.env.VITE_SENTRY_DSN) {
      this._size = `${w}x${h} @ ${dpr}x`;
      this.captureSentryContext();
    }
    await this.workerPuzzle.resizeDrawing({ w, h }, dpr);
  }

  public async setDrawingPalette(colors: string[]): Promise<void> {
    await this.workerPuzzle.setDrawingPalette(colors);
  }

  public async setDrawingFontInfo(fontInfo: FontInfo): Promise<void> {
    await this.workerPuzzle.setDrawingFontInfo(fontInfo);
  }

  public async getImage(options?: ImageEncodeOptions): Promise<Blob> {
    return this.workerPuzzle.getImage(options);
  }

  /**
   * Place an image of the current puzzle on the clipboard.
   * This must be called from within a user event handler.
   * (And in Safari, there can't be any intervening async calls in that handler.)
   */
  public copyImage(type: string = "image/png") {
    // For Safari's "transient user activation" security policy, the call to
    // clipboard.write must be synchronous (but the data can be a promise).
    const blobPromise = this.getImage({ type });
    return navigator.clipboard.write([new ClipboardItem({ [type]: blobPromise })]);
  }

  //
  // Timer state
  //

  // Pending while timer active; resolves when deactivated
  public timerComplete: Promise<void> = Promise.resolve();
  private timerCompleteResolve?: () => void;

  private notifyTimerState = (isActive: boolean) => {
    // Resolve the current activation (if any)
    this.timerCompleteResolve?.();
    this.timerCompleteResolve = undefined;
    if (isActive) {
      // Start a new activation cycle
      this.timerComplete = new Promise<void>((resolve) => {
        this.timerCompleteResolve = resolve;
      });
    }
  };
}
